import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import styled from 'styled-components';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

const NewsFeedContainer = styled.div`
  width: 100%;
  padding: 60px 0;
  background: linear-gradient(to bottom, #f8f9fa, #e9ecef);
`;

const NewsFeedTitle = styled.h2`
  text-align: center;
  color: #2E7D32;
  font-size: 2.5rem;
  margin-bottom: 40px;
  font-weight: 700;
`;

const NewsFeedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NewsCard = styled.div`
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
  }
`;

const NewsImageContainer = styled.div`
  width: 100%;
  height: 220px;
  position: relative;
  overflow: hidden;
  background-color: #f3f3f3;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NewsImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  ${NewsCard}:hover & {
    transform: scale(1.05);
  }
`;

const NewsContent = styled.div`
  padding: 20px;
`;

const NewsDate = styled.p`
  font-size: 0.9rem;
  color: #777;
  margin-bottom: 10px;
`;

const NewsText = styled.p`
  font-size: 1rem;
  color: #333;
  line-height: 1.6;
`;

const LoadingSpinner = styled(AiOutlineLoading3Quarters)`
  animation: spin 1s linear infinite;
  font-size: 2rem;
  color: #2E7D32;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadMoreButton = styled.button`
  background: linear-gradient(to right, #2E7D32, #1B5E20);
  color: white;
  border: none;
  border-radius: 25px;
  padding: 12px 30px;
  font-size: 1rem;
  font-weight: 600;
  margin: 40px auto 0;
  display: block;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(to right, #1B5E20, #0A3F0A);
    transform: translateY(-3px);
    box-shadow: 0 4px 10px rgba(0,0,0,0.2);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const NoNewsMessage = styled.p`
  text-align: center;
  color: #777;
  font-size: 1.2rem;
  margin: 40px 0;
`;

const NewsFeed = ({ limit }) => {
  const navigate = useNavigate();
  const [newsPosts, setNewsPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [postWithImagesLoaded, setPostWithImagesLoaded] = useState({});
  const observer = useRef();
  const postsPerPage = limit || 6;
  
  // Handle click on news card
  const handleNewsCardClick = (postId) => {
    navigate(`/news/${postId}`);
  };

  // Fetch news posts
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const posts = await apiService.getNewsPosts();
        setNewsPosts(posts);
        setHasMore(posts.length > postsPerPage);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching news posts:', error);
        setLoading(false);
      }
    };

    fetchPosts();
  }, [postsPerPage]);

  // Lazy load images for a post when it becomes visible
  const loadPostImages = async (postId) => {
    // Check if we've already loaded or are loading images for this post
    if (postWithImagesLoaded[postId] === 'loading' || postWithImagesLoaded[postId] === 'loaded') {
      return;
    }

    setPostWithImagesLoaded(prev => ({ ...prev, [postId]: 'loading' }));
    try {
      const images = await apiService.getNewsPostImages(postId);
      setNewsPosts(prev => 
        prev.map(post => 
          post._id === postId ? { ...post, images } : post
        )
      );
      setPostWithImagesLoaded(prev => ({ ...prev, [postId]: 'loaded' }));
    } catch (error) {
      console.error(`Error loading images for post ${postId}:`, error);
      setPostWithImagesLoaded(prev => ({ ...prev, [postId]: 'error' }));
    }
  };

  // Create observers for each post
  useEffect(() => {
    if (loading) return;
    
    // Create a new observer instance
    const observerInstance = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.target.dataset.postId) {
          loadPostImages(entry.target.dataset.postId);
        }
      });
    }, { rootMargin: '100px' });
    
    // Find all news card containers and observe them
    const newsContainers = document.querySelectorAll('[data-post-id]');
    newsContainers.forEach(container => {
      observerInstance.observe(container);
    });
    
    // Cleanup function
    return () => {
      if (observerInstance) {
        observerInstance.disconnect();
      }
    };
  }, [loading, newsPosts]);

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Load more posts
  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  // Calculate which posts to display based on current page
  const visiblePosts = newsPosts.slice(0, page * postsPerPage);

  return (
    <NewsFeedContainer>
      <NewsFeedTitle>Latest News</NewsFeedTitle>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <LoadingSpinner />
        </div>
      ) : newsPosts.length === 0 ? (
        <NoNewsMessage>No news posts available at the moment.</NoNewsMessage>
      ) : (
        <>
          <NewsFeedGrid>
            {visiblePosts.map((post, index) => (
              <NewsCard 
                key={post._id}
                onClick={() => handleNewsCardClick(post._id)}
              >
                <NewsImageContainer
                  data-post-id={post._id}
                >
                  {postWithImagesLoaded[post._id] === 'loading' || !postWithImagesLoaded[post._id] ? (
                    <LoadingSpinner />
                  ) : post.images && post.images.length > 0 ? (
                    <NewsImage src={post.images[0]} alt="News post" />
                  ) : (
                    <div>No image available</div>
                  )}
                </NewsImageContainer>
                <NewsContent>
                  <NewsDate>{formatDate(post.postDate || post.createdAt)}</NewsDate>
                  <NewsText>{post.text}</NewsText>
                </NewsContent>
              </NewsCard>
            ))}
          </NewsFeedGrid>
          
          {hasMore && page * postsPerPage < newsPosts.length && (
            <LoadMoreButton onClick={handleLoadMore} disabled={loading}>
              {loading ? 'Loading...' : 'Load More'}
            </LoadMoreButton>
          )}
        </>
      )}
    </NewsFeedContainer>
  );
};

export default NewsFeed;
